import { Transport, type TransportOptions } from 'sip.js/lib/platform/web';
import { TransportState } from 'sip.js/lib/api';
import type { Logger } from 'sip.js/lib/core';

export interface ServerRotation {
  index: number;
}

export interface HaTransportOptions extends TransportOptions {
  servers?: string[];
  rotation?: ServerRotation;
  watchdogTimeout?: number;
}

const DEFAULT_WATCHDOG_MS = 6000;
const MIN_WATCHDOG_MS = 4000;

// Added because sip.js doesn't support multiple wss servers (e.g. servers: ['wss://kamailio1.example.com', 'wss://kamailio2.example.com']); consider switching to JsSIP in the future, which does.
export class HaTransport extends Transport {
  private readonly servers: string[];
  private readonly rotation: ServerRotation;
  private readonly watchdogTimeoutMs: number;
  private watchdogTimer: ReturnType<typeof setTimeout> | undefined;
  private lastState: TransportState;
  private readonly log: Logger;

  constructor(logger: Logger, options: HaTransportOptions) {
    super(logger, options);

    this.log = logger;
    this.servers = options.servers && options.servers.length > 0 ? options.servers.slice() : [options.server];
    this.rotation = options.rotation ?? { index: 0 };
    this.watchdogTimeoutMs = Math.max(MIN_WATCHDOG_MS, options.watchdogTimeout ?? DEFAULT_WATCHDOG_MS);
    this.lastState = this.state;

    this.stateChange.addListener((state) => this.handleStateChange(state));
  }

  override get server(): string {
    if (this.servers.length === 0) {
      return super.server;
    }
    return this.servers[this.rotation.index % this.servers.length]!;
  }

  override dispose(): Promise<void> {
    this.disarmWatchdog();
    return super.dispose();
  }

  private handleStateChange(state: TransportState): void {
    switch (state) {
      case TransportState.Connecting:
        this.armWatchdog();
        break;
      case TransportState.Connected:
        this.disarmWatchdog();
        break;
      case TransportState.Disconnected:
        this.disarmWatchdog();
        if (this.lastState === TransportState.Connecting || this.lastState === TransportState.Connected) {
          this.rotateServer();
        }
        break;
      default:
        break;
    }
    this.lastState = state;
  }

  private armWatchdog(): void {
    this.disarmWatchdog();
    this.watchdogTimer = setTimeout(() => {
      this.watchdogTimer = undefined;
      if (this.state === TransportState.Connected) {
        return;
      }
      const zombie = this.ws;
      this.log.warn(
        `[HaTransport] Connect watchdog fired after ${this.watchdogTimeoutMs}ms with no OPEN on ${this.server}. ` +
          `Force-closing the zombie socket to trigger failover.`
      );
      if (zombie) {
        try {
          zombie.close();
        } catch {
          /* close() only throws on bad args; ignore */
        }
      }
    }, this.watchdogTimeoutMs);
  }

  private disarmWatchdog(): void {
    if (this.watchdogTimer !== undefined) {
      clearTimeout(this.watchdogTimer);
      this.watchdogTimer = undefined;
    }
  }

  private rotateServer(): void {
    if (this.servers.length <= 1) {
      return;
    }
    this.rotation.index = (this.rotation.index + 1) % this.servers.length;
    this.log.warn(`[HaTransport] Rotated to server[${this.rotation.index}] = ${this.servers[this.rotation.index]}`);
  }
}
